
import React from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

interface RichTextViewerProps {
  content: string;
  className?: string;
  loading?: boolean;
  preserveContentWhileLoading?: boolean;
}

export const RichTextViewer = ({ 
  content, 
  className, 
  loading = false,
  preserveContentWhileLoading = false
}: RichTextViewerProps) => {
  // If we're loading but have existing content and want to preserve it
  if (loading && content && preserveContentWhileLoading) {
    return (
      <div 
        className={cn("prose prose-sm sm:prose-base lg:prose-lg max-w-none dark:prose-invert relative", className)}
      >
        {/* Show content with subtle loading overlay */}
        <div className="opacity-70" dangerouslySetInnerHTML={{ __html: content }} />
        <div className="absolute inset-0 bg-background/30 pointer-events-none" />
      </div>
    );
  }
  
  // Regular loading state with no content to preserve
  if (loading) {
    return (
      <div className={cn("prose prose-sm sm:prose-base lg:prose-lg max-w-none", className)}>
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-6 w-4/5 mb-4" />
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-6 w-full mb-2" />
        <Skeleton className="h-6 w-3/4" />
      </div>
    );
  }
  
  if (!content) return null;
  
  return (
    <div 
      className={cn(
        "prose prose-sm sm:prose-base lg:prose-lg max-w-none dark:prose-invert prose-headings:text-foreground prose-p:text-foreground",
        // Table-specific styles
        "prose-table:border-collapse prose-table:border prose-table:border-border",
        "prose-th:border prose-th:border-border prose-th:bg-muted prose-th:p-2 prose-th:text-left prose-th:font-semibold",
        "prose-td:border prose-td:border-border prose-td:p-2",
        // Center tables by default and keep natural width
        "prose-table:mx-auto prose-table:w-auto",
        className
      )}
      dangerouslySetInnerHTML={{ __html: content }} 
    />
  );
};
